import { useEffect, useMemo, useRef, useState } from "react";
import {
  RegisterOptions,
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  ToneSegments,
} from "../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
  SelectForm,
} from "../../../../components/Input";
import { buildDraftDetails } from "../../../../components/PopUp/confirmShared";
import { localToday, compareDecimalStrings } from "../../../../utils";
import {
  normalizeQuantity,
  tradeDateFloor,
  validateAmount,
  validateQuantity,
  validateTradeDate,
} from "../tradeValidation";
import { ChargeField } from "../ChargeField";
import { NoteField } from "../NoteField";
import { settlementNoteOf } from "../settlementNote";
import { MobileHoldingTradeForm } from "./MobileHoldingTradeForm";
import {
  ACTION_SEGMENTS,
  buildHoldingOptions,
  createDefaultHoldingTrade,
  FEE_HELPER,
  FORM_ID,
  HOLDING_TRADE_COPY,
  tradeNetCentsOf,
  WITHHOLDING_TAX_HELPER,
} from "./constants";
import {
  HoldingOption,
  HoldingTradeAction,
  HoldingTradeFormValues,
  HoldingTradePanelProps,
} from "./types";
import { FieldWarning } from "./styles";

/**
 * One panel drives sell/dividend/fee/tax since they share most fields; the
 * server 400s a dividend carrying `quantity`, so payload branching stays in the page that owns the mutations.
 */
export const HoldingTradePanel = ({
  isOpen,
  onClose,
  investmentName,
  investmentStartDate,
  holdings,
  initialAction,
  initialHolding,
  settlementAccountName,
  handleOnSubmit,
}: HoldingTradePanelProps) => {
  const [panelError, setPanelError] = useState<string | null>(null);

  // The preselected position has to be the *option*, not the holding: the
  // select compares by `value`, and the payload needs `holdingId`.
  const defaultHoldingOption = useMemo<HoldingOption | null>(() => {
    if (!initialHolding) {
      return null;
    }
    const [option] = buildHoldingOptions([initialHolding], initialAction);
    return option ?? null;
  }, [initialHolding, initialAction]);

  const form = useForm<HoldingTradeFormValues>({
    defaultValues: createDefaultHoldingTrade(
      initialAction,
      defaultHoldingOption,
    ),
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, register, setValue, setError, clearErrors } =
    form;

  const { isSubmitting } = useFormState({ control });
  const action = useWatch({ control, name: "action" });
  const selectedHolding = useWatch({ control, name: "holding" });
  const enteredQuantity = useWatch({ control, name: "quantity" });
  const [amountEur, feeEur, taxWithheld] = useWatch({
    control,
    name: ["amountEur", "feeEur", "taxWithheld"],
  });

  const isSell = action === "sell";
  const isDividend = action === "dividend";
  const copy = HOLDING_TRADE_COPY[action];
  const settlementNote = settlementNoteOf({
    accountName: settlementAccountName,
    netCents: tradeNetCentsOf({ action, amountEur, feeEur, taxWithheld }),
  });

  const holdingOptions = useMemo(
    () => buildHoldingOptions(holdings, action),
    [holdings, action],
  );

  // An action change reshapes the form: quantity is sell-only, and a position
  // selectable for a dividend may not be offered for a sale.
  const previousAction = useRef<HoldingTradeAction>(initialAction);
  useEffect(() => {
    if (previousAction.current === action) {
      return;
    }
    previousAction.current = action;
    if (action !== "sell") {
      setValue("quantity", "");
      clearErrors("quantity");
    }
    // A hidden charge must not survive into the next action's payload.
    setValue("feeEur", "");
    setValue("taxWithheld", "");
    clearErrors(["feeEur", "taxWithheld"]);
    const holding = getValues("holding");
    const isStillOffered =
      holding &&
      holdingOptions.some((option) => option.value === holding.value);
    if (holding && !isStillOffered) {
      setValue("holding", null);
    }
  }, [action, holdingOptions, getValues, setValue, clearErrors]);

  const minDate = useMemo(
    () => tradeDateFloor(investmentStartDate),
    [investmentStartDate],
  );

  /**
   * Warning, never a block — a backdated sale can legitimately exceed today's
   * cached quantity after a reverse split. Compared as strings, since DECIMAL(28,6) exceeds `Number`'s safe range.
   */
  const overHeldWarning = useMemo(() => {
    if (!isSell || !selectedHolding) {
      return null;
    }
    const entered = normalizeQuantity(enteredQuantity);
    if (validateQuantity(entered) !== true) {
      return null;
    }
    if (compareDecimalStrings(entered, selectedHolding.quantity) <= 0) {
      return null;
    }
    return `You currently hold ${selectedHolding.quantity} units of ${selectedHolding.symbol}. Recording this is still allowed — a backdated sale before a corporate action can legitimately exceed it.`;
  }, [isSell, selectedHolding, enteredQuantity]);

  const handleActionChange = (next: HoldingTradeAction) => {
    setValue("action", next, { shouldDirty: true });
    setPanelError(null);
  };

  const onSubmit: SubmitHandler<HoldingTradeFormValues> = (data) => {
    setPanelError(null);
    return handleOnSubmit(data, { setError, setPanelError });
  };

  const buildDiscardDetails = () => {
    const values = getValues();
    return buildDraftDetails({
      noun: "trade",
      fieldValues: [
        values.holding?.symbol,
        values.quantity,
        values.amountEur,
        values.feeEur,
        values.taxWithheld,
        values.note,
      ],
      primaryLabel: values.holding?.symbol,
    });
  };

  const dateRules: RegisterOptions<HoldingTradeFormValues, "date"> = {
    required: "Date is required!",
    validate: (value: Date) => validateTradeDate(value, minDate),
  };

  // Sell-only: dividend and fee never send a quantity, so this must not block their submit.
  const quantityRules: RegisterOptions<HoldingTradeFormValues, "quantity"> = {
    validate: (value: string) => (isSell ? validateQuantity(value) : true),
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      error={panelError}
      ariaLabel={copy.ariaLabel}
      eyebrow={copy.eyebrow}
      title={copy.title(investmentName)}
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={copy.submitLabel}
      footerNote={settlementNote}
      discard={{
        title: copy.discardTitle,
        confirmLabel: "Discard",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: copy.compactTitle,
        submitLabel: copy.compactActionLabel,
        fields: (
          <MobileHoldingTradeForm
            control={control}
            register={register}
            setValue={setValue}
            action={action}
            copy={copy}
            holdingOptions={holdingOptions}
            minDate={minDate}
            dateRules={dateRules}
            quantityRules={quantityRules}
            overHeldWarning={overHeldWarning}
            isSubmitting={isSubmitting}
            onActionChange={handleActionChange}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Trade type *</FieldLabel>
          </LabelRow>
          <ToneSegments
            groupLabel="Trade type"
            options={ACTION_SEGMENTS}
            selectedValue={action}
            disabled={isSubmitting}
            onChange={(segment) => handleActionChange(segment.value)}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-trade-position">Position *</FieldLabel>
          </LabelRow>
          <SelectForm
            id="holding-trade-position"
            name="holding"
            control={control}
            rules={{ required: "Position is required!" }}
            defaultValue={null}
            options={holdingOptions}
            disabled={isSubmitting}
          />
          <FieldHelper>{copy.positionHelper}</FieldHelper>
        </Field>

        {isSell && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="holding-trade-quantity">
                Quantity *
              </FieldLabel>
            </LabelRow>
            <FormInput
              id="holding-trade-quantity"
              required
              name="quantity"
              control={control}
              rules={quantityRules}
              handleClear={false}
              disabled={isSubmitting}
              placeholder="e.g. 12.5"
            />
            <FieldHelper>Units sold, up to 6 decimal places.</FieldHelper>
            {overHeldWarning && (
              <FieldWarning role="status">{overHeldWarning}</FieldWarning>
            )}
          </Field>
        )}
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-trade-amount">Amount *</FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="holding-trade-amount"
            required
            name="amountEur"
            control={control}
            rules={{ validate: validateAmount }}
            disabled={isSubmitting}
            placeholder="0.00"
          />
          <FieldHelper>{copy.amountHelper}</FieldHelper>
        </Field>

        {isSell && (
          <ChargeField
            id="holding-trade-fee"
            control={control}
            charge="fee"
            helper={FEE_HELPER}
            isSubmitting={isSubmitting}
          />
        )}
        {isDividend && (
          <ChargeField
            id="holding-trade-tax"
            control={control}
            charge="withholdingTax"
            helper={WITHHOLDING_TAX_HELPER}
            isSubmitting={isSubmitting}
          />
        )}
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="holding-trade-date">Trade date *</FieldLabel>
          </LabelRow>
          <InputDate
            id="holding-trade-date"
            name="date"
            control={control}
            rules={dateRules}
            disabled={isSubmitting}
            minDate={minDate}
            maxDate={localToday()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>
            Must be on or after the account's start date.
          </FieldHelper>
        </Field>

        <NoteField
          id="holding-trade-note"
          control={control}
          isSubmitting={isSubmitting}
        />
      </FormCard>
    </FormPanel>
  );
};
